package com.arena_web.utils

import com.arena_web.data.api.PlacesApi
import kotlin.random.Random
import kotlinx.coroutines.CancellationException

/**
 * One row of the address autocomplete dropdown: either a Google Places
 * prediction (has [placeId]; coordinates are resolved on pick via the backend
 * proxy) or an already-loaded Nominatim hit ([nominatim], the fallback path
 * when the backend has no Google key or is unreachable).
 *
 * Same shape as the mobile `AddressOption` (GeoApi.kt) — keep them in sync.
 */
data class AddressPrediction(
    /** Main line, e.g. "Khreshchatyk Street, 22". */
    val title: String,
    /** Secondary line, e.g. "Kyiv, Ukraine" — empty when unknown. */
    val subtitle: String,
    val placeId: String? = null,
    val nominatim: AddressResult? = null,
)

// Google autocomplete session: one random token spans the keystroke series +
// the final place-details call, so Google bills them as ONE session instead
// of per request. Reset after each resolve; lazily re-created on next typing.
private var addressSessionToken: String? = null

private fun currentSession(): String =
    addressSessionToken
        ?: buildString(32) { repeat(32) { append("0123456789abcdef"[Random.nextInt(16)]) } }
            .also { addressSessionToken = it }

/**
 * Google Places via the backend proxy first; Nominatim when that yields
 * nothing (no key configured, backend down, not signed in, or genuinely no
 * hits — for a real query Google practically always returns something, so the
 * extra Nominatim call only happens on the degraded paths).
 *
 * Nominatim fallback results are NOT country-restricted, only RANKED: the
 * user's own country first, then nearest-first — a precise foreign address
 * stays findable. Google results keep Google's own bias-aware order.
 */
suspend fun autocompleteAddress(
    placesApi: PlacesApi?,
    query: String,
    biasLat: Double?,
    biasLng: Double?,
    userCountryName: String? = null,
): List<AddressPrediction> {
    if (placesApi != null) {
        val google = try {
            placesApi.autocomplete(query, biasLat, biasLng, currentSession()).data
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            console.log("Places autocomplete error: ${e.message}")
            null
        }
        if (!google.isNullOrEmpty()) {
            return google.map {
                AddressPrediction(title = it.title, subtitle = it.subtitle.orEmpty(), placeId = it.placeId)
            }
        }
    }

    return searchAddress(query, biasLat, biasLng)
        .sortedWith(
            compareBy(
                // Own-country results first.
                { if (userCountryName != null && it.country.equals(userCountryName, ignoreCase = true)) 0 else 1 },
                // Then nearest-first (squared lat/lng distance is enough to order).
                { r ->
                    if (biasLat != null && biasLng != null) {
                        val dLat = r.lat - biasLat
                        val dLng = r.lng - biasLng
                        dLat * dLat + dLng * dLng
                    } else {
                        0.0
                    }
                },
            ),
        )
        .map { AddressPrediction(title = it.title, subtitle = it.subtitle, nominatim = it) }
}

/**
 * Resolve a picked prediction to full coordinates + address parts. Nominatim
 * hits are already loaded; Google predictions cost one place-details call,
 * which also closes the billing session. Null when the lookup fails — leave
 * the field uncommitted so validation asks the user to pick again.
 */
suspend fun resolveAddressPrediction(
    placesApi: PlacesApi?,
    prediction: AddressPrediction,
): AddressResult? {
    prediction.nominatim?.let { return it }
    val placeId = prediction.placeId ?: return null
    val session = addressSessionToken
    addressSessionToken = null
    val place = try {
        placesApi?.placeDetails(placeId, session)?.data
    } catch (e: CancellationException) {
        throw e
    } catch (e: Exception) {
        console.log("Place details error: ${e.message}")
        null
    } ?: return null
    val joined = listOfNotNull(place.street, place.houseNumber, place.city, place.country)
        .filter { it.isNotBlank() }
        .joinToString(", ")
    return AddressResult(
        displayName = place.formattedAddress ?: joined.ifBlank { prediction.title },
        lat = place.latitude,
        lng = place.longitude,
        city = place.city,
        street = place.street,
        houseNumber = place.houseNumber,
        country = place.country,
    )
}
